import { t } from "@lingui/macro";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import BookmarkBorderOutlinedIcon from "@mui/icons-material/BookmarkBorderOutlined";
import CodeOutlinedIcon from "@mui/icons-material/CodeOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import {
  Alert, Box, Paper, Snackbar, Stack, Tab, Tabs, Typography,
} from "@mui/material";
import { useEffect, useRef } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import BookmarksView from "./views/BookmarksView";
import CoursesView from "./views/CoursesView";
import CompilerView from "./views/CompilerView";
import CoursePlayerView from "./views/CoursePlayerView";
import DashboardView from "./views/DashboardView";
import LeaderboardView from "./views/LeaderboardView";
import ProblemExplorerView from "./views/ProblemExplorerView";
import ProblemWorkspaceView from "./views/ProblemWorkspaceView";
import SkillTestsView from "./views/SkillTestsView";
import SkillTestPlayerView from "./views/SkillTestPlayerView";
import SkillTestSubmissionsView from "./views/SkillTestSubmissionsView";
import SubmissionsView from "./views/SubmissionsView";
import TracksView from "./views/TracksView";
import { LoadingBlock, PALETTE } from "./components/shared";
import { type CPView, useCodingPlatform } from "./hooks/useCodingPlatform";

const NAV_ITEMS: { view: CPView; icon: React.ReactNode }[] = [
  { view: "Dashboard",   icon: <GridViewOutlinedIcon sx={{ fontSize: 16 }} /> },
  { view: "Courses",     icon: <AutoStoriesOutlinedIcon sx={{ fontSize: 16 }} /> },
  { view: "SkillTests",  icon: <QuizOutlinedIcon sx={{ fontSize: 16 }} /> },
  { view: "Tracks",      icon: <TrendingUpOutlinedIcon sx={{ fontSize: 16 }} /> },
  { view: "Problems",    icon: <CodeOutlinedIcon sx={{ fontSize: 16 }} /> },
  { view: "Compiler",    icon: <TerminalOutlinedIcon sx={{ fontSize: 16 }} /> },
  { view: "Submissions", icon: <HistoryOutlinedIcon sx={{ fontSize: 16 }} /> },
  { view: "Bookmarks",   icon: <BookmarkBorderOutlinedIcon sx={{ fontSize: 16 }} /> },
];

const encodeForIde = (problemId: string, studentId: string) =>
  btoa(JSON.stringify({ type: "coding-platform", problemId, studentId }));

export default function CodingPlatformPage() {
  // ── All params & hooks first (no hooks after early returns) ───────────────
  const {
    tab,
    courseId, lessonId,
    testId,
    problemId,
  } = useParams<{ tab?: string; courseId?: string; lessonId?: string; testId?: string; problemId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const cp = useCodingPlatform();
  const prevViewRef = useRef(cp.view);

  const navLabels: Record<CPView, string> = {
    Dashboard: t`Dashboard`,
    Courses: t`Courses`,
    SkillTests: t`Skill Tests`,
    Tracks: t`Tracks`,
    Problems: t`Problems`,
    Compiler: t`Compiler`,
    Submissions: t`Submissions`,
    Bookmarks: t`Bookmarks`,
    Leaderboard: t`Leaderboard`,
  };

  useEffect(() => {
    if (prevViewRef.current === cp.view) return;
    prevViewRef.current = cp.view;
    if (cp.view === "Dashboard") cp.setProblemPageSize(5);
    else if (cp.view === "Problems") cp.setProblemPageSize(10);
  }, [cp.view]);

  // Auto-load problem data when landing directly on a problem URL (e.g. refresh)
  useEffect(() => {
    if (problemId && !cp.selectedProblem && cp.studentId) {
      cp.loadProblem(problemId);
    }
  }, [problemId, cp.studentId]);

  // ── Navigation helpers ─────────────────────────────────────────────────────
  const openCourse = (id: string) => navigate(`/coding-platform/courses/${id}`);
  const openTest = (id: string) => navigate(`/coding-platform/skill-tests/${id}`);
  const openTestSubmissions = (id: string) => navigate(`/coding-platform/skill-tests/${id}/submissions`);
  const openProblem = async (id: string) => {
    await cp.loadProblem(id);
    navigate(`/coding-platform/problems/${id}`);
  };

  const activeProblem = cp.selectedProblem ?? cp.problems[0] ?? null;

  const openFullscreenIde = (problem: any) => {
    const id = problem?.id ?? problem?._id;
    if (!id || !cp.studentId) return;
    window.open(
      `${import.meta.env.VITE_INTERVIEW_WEB_APP_URL}/self-coding?data=${encodeForIde(id, cp.studentId)}`,
      "_blank", "noopener,noreferrer",
    );
  };

  // ── Derive which overlay is active from the URL ────────────────────────────
  const isTestSubmissions = !!testId && location.pathname.endsWith("/submissions");

  // ── Early returns AFTER all hooks ─────────────────────────────────────────

  // Bare /coding-platform → redirect to dashboard
  if (!tab && !courseId && !testId && !problemId) {
    return <Navigate to="/coding-platform/dashboard" replace />;
  }

  // Course player (with optional lesson)
  if (courseId) {
    return (
      <Box sx={{ flexGrow: 1, px: 1, height: "100%", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <CoursePlayerView
          courseId={courseId}
          initialLessonId={lessonId}
          onBack={() => navigate("/coding-platform/courses")}
          onLessonOpen={(cId, lId) => navigate(`/coding-platform/courses/${cId}/lesson/${lId}`)}
          onLessonClose={(cId) => navigate(`/coding-platform/courses/${cId}`)}
        />
      </Box>
    );
  }

  // Skill test submissions
  if (testId && isTestSubmissions) {
    return (
      <Box sx={{ flexGrow: 1, px: 1 }}>
        <SkillTestSubmissionsView
          testId={testId}
          studentId={cp.studentId}
          onBack={() => navigate("/coding-platform/skill-tests")}
          onOpenTest={(id) => navigate(`/coding-platform/skill-tests/${id}`)}
          backLabel="Back to Skill Tests"
        />
      </Box>
    );
  }

  // Skill test player
  if (testId) {
    return (
      <Box sx={{ flexGrow: 1, px: 1 }}>
        <SkillTestPlayerView
          testId={testId}
          onBack={() => navigate("/coding-platform/skill-tests")}
        />
      </Box>
    );
  }

  // Problem workspace
  if (problemId) {
    return (
      <Box sx={{ flexGrow: 1, px: 1 }}>
        <ProblemWorkspaceView
          problem={activeProblem}
          language={cp.language}
          code={cp.code}
          customInput={cp.customInput}
          execution={cp.execution}
          running={cp.running}
          onLanguageChange={(l) => cp.setLanguage(l)}
          onCodeChange={cp.setCode}
          onCustomInputChange={cp.setCustomInput}
          onRun={() => cp.execute(false)}
          onSubmit={() => cp.execute(true)}
          onToggleBookmark={cp.toggleBookmark}
          onOpenIde={openFullscreenIde}
          onBack={() => navigate("/coding-platform/problems")}
        />
        <Snackbar open={!!cp.notice} autoHideDuration={4000} onClose={() => cp.setNotice(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
          <Alert severity={cp.notice?.type ?? "info"} onClose={() => cp.setNotice(null)} sx={{ fontSize: "13px" }}>{cp.notice?.message}</Alert>
        </Snackbar>
      </Box>
    );
  }

  // ── Tab shell ──────────────────────────────────────────────────────────────
  const renderView = () => {
    if (cp.loading) return <LoadingBlock message="Loading Coding Platform..." />;
    switch (cp.view) {
      case "Dashboard":
        return (
          <DashboardView
            dashboard={cp.dashboard}
            profile={cp.profile}
            notifications={cp.notifications}
            submissions={cp.submissions}
            skillTestSubmissions={cp.skillTestSubmissions}
            recentlyViewedCourses={cp.recentlyViewedCourses}
            tracks={cp.tracks}
            leaderboard={cp.leaderboard}
            studentId={cp.studentId}
            onNavigate={cp.setView}
            onLoadProblem={cp.loadProblem}
            onOpenCourse={openCourse}
          />
        );
      case "Courses":
        return <CoursesView onOpenCourse={openCourse} onBookmarkChange={() => cp.refreshCourseBookmarks()} />;
      case "SkillTests":
        return <SkillTestsView onOpenTest={openTest} />;
      case "Problems":
        return (
          <ProblemExplorerView
            problems={cp.problems}
            facets={cp.facets}
            total={cp.problemTotal}
            page={cp.problemPage}
            rowsPerPage={cp.problemLimit}
            activeProblemId={activeProblem?.id ?? activeProblem?._id}
            filters={cp.filters}
            loading={cp.loading}
            onApplyFilters={cp.applyFilters}
            onPageChange={cp.changeProblemPage}
            onRowsPerPageChange={cp.setProblemPageSize}
            onLoadProblem={(id) => openProblem(id)}
            onToggleBookmark={cp.toggleBookmark}
          />
        );
      case "Submissions":
        return (
          <SubmissionsView
            problemSubmissions={cp.submissions}
            skillTestSubmissions={cp.skillTestSubmissions}
          />
        );
      case "Bookmarks":
        return (
          <BookmarksView
            bookmarks={cp.bookmarks}
            courseBookmarks={cp.courseBookmarks}
            onLoadProblem={(id) => openProblem(id)}
            onOpenCourse={openCourse}
            onToggleBookmark={cp.toggleBookmark}
            onToggleCourseBookmark={cp.toggleCourseBookmark}
            onEnrollCourse={cp.enrollCourse}
          />
        );
      case "Tracks":
        return (
          <TracksView
            tracks={cp.tracks}
            onOpenCourse={openCourse}
            onOpenTest={openTest}
            onViewTestSubmissions={openTestSubmissions}
          />
        );
      case "Leaderboard":  return <LeaderboardView leaderboard={cp.leaderboard} currentStudentId={cp.studentId} />;
      case "Compiler":     return <CompilerView />;
      default:             return null;
    }
  };

  return (
    <Box sx={{ flexGrow: 1, px: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography sx={{ fontSize: "15px", fontWeight: 700, color: "primary.main", px: 1 }}>{t`Coding Platform`}</Typography>
      </Box>

      <Paper sx={{ borderRadius: "12px", border: `1px solid ${PALETTE.border}`, boxShadow: "0px 1px 4px rgba(0,0,0,0.06)", mb: 2, overflow: "hidden" }}>
        <Tabs
          value={cp.view}
          onChange={(_e, v) => cp.setView(v as CPView)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ minHeight: "42px", bgcolor: PALETTE.bgLight, "& .MuiTab-root": { minHeight: "42px", textTransform: "none", fontSize: "13px", fontWeight: 600, px: 2 }, "& .MuiTabs-indicator": { height: 3, borderRadius: "3px 3px 0 0" } }}
        >
          {NAV_ITEMS.map(({ view, icon }) => (
            <Tab key={view} value={view} label={<Stack direction="row" spacing={0.75} alignItems="center">{icon}<span>{navLabels[view]}</span></Stack>} />
          ))}
        </Tabs>
      </Paper>

      {renderView()}

      <Snackbar open={!!cp.notice} autoHideDuration={4000} onClose={() => cp.setNotice(null)} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={cp.notice?.type ?? "info"} onClose={() => cp.setNotice(null)} sx={{ fontSize: "13px" }}>{cp.notice?.message}</Alert>
      </Snackbar>
    </Box>
  );
}
